import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { OtherExpense, OtherExpenseFormData } from "./types";
import { otherExpenseApi } from "./apiService";
import { otherExpenseValidationSchema } from "./validationSchema";
import { useToast } from "../../components/Toast";
import ExpenseForm from "./components/ExpenseForm";

const EditOtherExpense = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  
  const [expense, setExpense] = useState<OtherExpense | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchExpense();
    }
  }, [id]);

  const fetchExpense = async () => {
    try {
      setLoading(true);
      const data = await otherExpenseApi.getExpenseById(id!);
      setExpense(data);
    } catch (error) {
      console.error("Error fetching expense:", error);
      setError("Failed to fetch expense data");
      showToast("Failed to load expense", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: OtherExpenseFormData) => {
    try {
      setSubmitting(true);
      await otherExpenseApi.updateExpense(id!, values);
      showToast("Expense updated successfully", "success");
      navigate("/other-expense");
    } catch (error) {
      console.error("Error updating expense:", error);
      showToast("Failed to update expense", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate("/other-expense");
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !expense) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || "Expense not found"}
        </Alert>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Back to Expenses
        </Button>
      </Container>
    );
  }

  const initialValues: OtherExpenseFormData = {
    expenseDate: expense.expenseDate,
    category: expense.category,
    paidTo: expense.paidTo,
    amount: expense.amount.toString(),
    contactNumber: expense.contactNumber || "",
    location: expense.location || "",
    notes: expense.notes || "",
    expenseType: expense.expenseType,
    payments: expense.payments || [],
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
        >
          Back to Expenses
        </Button>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Edit Expense
        </Typography>
      </Box>

      {/* Form */}
      <Paper elevation={3} sx={{ p: 4 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={otherExpenseValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isValid, dirty, ...formikProps }) => (
            <Form>
              {/* <ExpenseForm index={0} /> */}
              
              {/* Submit Button */}
              <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={!isValid || !dirty || submitting}
                >
                  {submitting ? "Updating..." : "Update Expense"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default EditOtherExpense; 